import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { api } from '../api/apiClient';

interface SubscribeCardProps {
    domain: string;
    appName: string;
}

export function SubscribeCard({ domain, appName }: SubscribeCardProps) {
    const [subscribeOpen, setSubscribeOpen] = useState(false);
    const [subscribeEmail, setSubscribeEmail] = useState('');
    const [subscribing, setSubscribing] = useState(false);
    const [subscribeError, setSubscribeError] = useState<string | null>(null);
    const [subscribeSuccess, setSubscribeSuccess] = useState(false);
    // Held separately so the success message can still name the address after the input is cleared.
    const [submittedEmail, setSubmittedEmail] = useState('');

    async function handleSubscribe() {
        setSubscribeError(null);
        if (!subscribeEmail || !subscribeEmail.includes('@')) {
            setSubscribeError('Please enter a valid email address.');
            return;
        }
        setSubscribing(true);
        try {
            const res = await api.post('/api/subscribe', {
                email: subscribeEmail, appName, domain,
            });
            if (!res.ok) {
                const body = await res.json().catch(() => null);
                const msg = body?.errors?.[0]?.message ?? 'Something went wrong. Please try again.';
                setSubscribeError(msg);
                return;
            }
            setSubmittedEmail(subscribeEmail);
            setSubscribeSuccess(true);
        } catch {
            setSubscribeError('Something went wrong. Please try again.');
        } finally {
            setSubscribing(false);
        }
    }

    function handleSubscribeClose() {
        setSubscribeOpen(false);
        setSubscribeEmail('');
        setSubmittedEmail('');
        setSubscribeError(null);
        setSubscribeSuccess(false);
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, p: 2.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant='body1' sx={{ flexGrow: 1 }}>
                    Want more help like this? Subscribe to get notified when new content is posted.
                </Typography>
                <Button variant='contained' onClick={() => setSubscribeOpen(true)}>Subscribe</Button>
            </Box>

            <Dialog open={subscribeOpen} onClose={handleSubscribeClose} maxWidth='xs' fullWidth>
                <DialogTitle>{subscribeSuccess ? 'Check your inbox' : 'Subscribe for updates'}</DialogTitle>
                <DialogContent>
                    {subscribeSuccess ? (
                        <>
                            <Typography sx={{ py: 1 }}>
                                We sent a confirmation link to <strong>{submittedEmail}</strong>. Click it to activate
                                your subscription — the link is good for 7 days.
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                Don't see it? Check your spam folder.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                Enter your email and we'll notify you when new content is posted.
                            </Typography>
                            <TextField
                                label='Email address'
                                type='email'
                                value={subscribeEmail}
                                onChange={e => setSubscribeEmail(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSubscribe(); }}
                                fullWidth
                                autoFocus
                                error={Boolean(subscribeError)}
                                helperText={subscribeError ?? ''}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubscribeClose}>
                        {subscribeSuccess ? 'Close' : 'Cancel'}
                    </Button>
                    {!subscribeSuccess && (
                        <Button variant='contained' onClick={handleSubscribe} disabled={subscribing}>
                            {subscribing ? <CircularProgress size={18} /> : 'Subscribe'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
